import { useState } from "react";
import { mergeStyles, Stack } from "@fluentui/react";
import {
  Tab,
  TabList,
  SelectTabEvent,
  SelectTabData,
} from "@fluentui/react-components";
import { styles } from "./experimentDetailTabs.style";
import { styles as sharedStyles } from "../../../Shared/shared.styles";
import ExperimentDetailsForm from "../ExperimentDetailsForm/ExperimentDetailsForm";
import VariantDetailsForm from "../VariantDetailsForm/VariantDetailsForm";
import AdvancedFiltersForm from "../AdvancedFiltersForm/AdvancedFiltersForm";
import { FormTabLabel } from "../FormComponents";
import { ExperimentType } from "../../../Types";
import { ExperimentFormContext } from "../../../Hooks/UseExperimentForm";
import {
  EXPERIMENT_FIELDS,
  VARIANT_FIELDS,
  ADVANCED_FILTERS_FIELDS,
} from "../ExperimentDetailsForm/ExperimentDetailsValidation/experimentDetailsFormValidation";
import useAppInsights from "../../../Hooks/UseAppInsights";

/**
 * Props for the ExperimentDetailTabs component
 */
interface ExperimentDetailTabsProps {
  /**
   * Form context containing form state, handlers, and validation logic
   */
  experimentFormContext: ExperimentFormContext;
  /**
   * List of all experiments for validation and context
   */
  experiments: ExperimentType[];
  /**
   * Validation errors keyed by field name. Used to show error counts on tabs.
   */
  errors?: Record<string, string[]>;
  /**
   * When true, forms are editable. When false, forms are read-only.
   */
  isEditMode?: boolean;
  /**
   * When true, form will have empty fields for creating a new experiment
   */
  isCreate?: boolean;
  /**
   * When true, form will have pre-filled fields for cloning an existing experiment
   */
  isClone?: boolean;
}

// Tab key constants for consistent referencing
const EXPERIMENT_KEY = "experiment";
const VARIANT_KEY = "variant";
const ADVANCED_FILTERS_KEY = "advancedFilters";

/**
 * Tabbed interface for experiment details form. Manages four main form sections:
 * Experiment, Variants, Advanced Filters, and Extension Settings. Tab labels show error
 * counts and change indicators.
 */
const ExperimentDetailTabs: React.FC<ExperimentDetailTabsProps> = ({
  experimentFormContext,
  experiments,
  isEditMode = false,
  isCreate = false,
  isClone = false,
  errors = {},
}) => {
  const [selectedTab, setSelectedTab] = useState<string>(EXPERIMENT_KEY);
  const { trackOperationEvent } = useAppInsights("ExperimentDetailTabs");

  const onTabSelected = (_event: SelectTabEvent, data: SelectTabData) => {
    const selected = data.value as string;
    setSelectedTab(selected);
    trackOperationEvent({
      operation: "TabChange",
      function: "onTabSelected",
      properties: { selectedTab: selected },
    });
  };

  const experimentSelected = selectedTab === EXPERIMENT_KEY;
  const variantSelected = selectedTab === VARIANT_KEY;
  const advancedFiltersSelected = selectedTab === ADVANCED_FILTERS_KEY;
  const {
    formChanges: { experimentChanges, variantChanges, advancedFilterChanges },
  } = experimentFormContext;
  return (
    <Stack className={sharedStyles.flexOverflowContainer}>
      <TabList
        onTabSelect={onTabSelected}
        defaultSelectedValue={EXPERIMENT_KEY}
        className={styles.tabsListContainer}
      >
        <Tab id={EXPERIMENT_KEY} value={EXPERIMENT_KEY}>
          <FormTabLabel
            label="Experiment"
            fields={EXPERIMENT_FIELDS}
            errors={errors}
            isSelected={experimentSelected}
            changesCount={experimentChanges}
          />
        </Tab>
        <Tab id={VARIANT_KEY} value={VARIANT_KEY}>
          <FormTabLabel
            label="Variants"
            fields={VARIANT_FIELDS}
            errors={errors}
            isSelected={variantSelected}
            changesCount={variantChanges}
            hideChangeTotal={true}
          />
        </Tab>
        <Tab id={ADVANCED_FILTERS_KEY} value={ADVANCED_FILTERS_KEY}>
          <FormTabLabel
            label="Advanced Filters"
            fields={ADVANCED_FILTERS_FIELDS}
            errors={errors}
            isSelected={advancedFiltersSelected}
            changesCount={advancedFilterChanges}
          />
        </Tab>
      </TabList>
      <Stack
        className={mergeStyles(
          sharedStyles.scrollbarThin,
          styles.panelsContainer,
        )}
      >
        {experimentSelected && (
          <ExperimentDetailsForm
            experimentFormContext={experimentFormContext}
            isEditMode={isEditMode}
            errors={errors}
          />
        )}
        {variantSelected && (
          <VariantDetailsForm
            experimentFormContext={experimentFormContext}
            experiments={experiments}
            errors={errors}
            isEditMode={isEditMode}
            isCreate={isCreate}
            isClone={isClone}
          />
        )}
        {advancedFiltersSelected && (
          <AdvancedFiltersForm
            experimentFormContext={experimentFormContext}
            errors={errors}
            isEditMode={isEditMode}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default ExperimentDetailTabs;
